import React, { useState, useEffect } from 'react';
import Parse from 'parse';
import { BethelAppClasses } from '../../api/config';

const PrayerRequestsManager = () => {
    const [prayerRequests, setPrayerRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('all'); // all, unanswered, answered, private
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchPrayerRequests();
    }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchPrayerRequests = async () => {
        try {
            setLoading(true);
            setError('');

            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            const PrayerRequest = Parse.Object.extend(BethelAppClasses.PrayerRequest);
            const query = new Parse.Query(PrayerRequest);
            
            // Apply filters
            if (filter === 'unanswered') {
                query.notEqualTo('prayed', true);
            } else if (filter === 'answered') {
                query.equalTo('prayed', true);
            } else if (filter === 'private') {
                query.equalTo('private', true);
            }

            query.include('user');
            query.descending('createdAt');
            query.limit(500);

            const requests = await query.find();
            setPrayerRequests(requests);

            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } catch (err) {
            console.error('Error fetching prayer requests:', err);
            setError('Failed to load prayer requests. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        } finally {
            setLoading(false);
        }
    };

    const markAsAnswered = async (requestId) => {
        try {
            // Save current Parse configuration
            const originalServerURL = Parse.serverURL;
            const originalAppId = Parse.applicationId;
            
            // Switch to Bethel app server
            Parse.serverURL = 'https://app.bethelchristiancenter.com/parse';
            Parse._initialize('bethel', null, null);

            const result = await Parse.Cloud.run('markPrayerAnswered', { 
                prayerRequestId: requestId 
            });
            
            // Restore original Parse configuration
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
            
            if (result.success) {
                // Update local state
                setPrayerRequests(prev => 
                    prev.map(req => {
                        if (req.id === requestId) {
                            req.set('prayed', true);
                        }
                        return req;
                    })
                );
            }
        } catch (err) {
            console.error('Error marking prayer as answered:', err);
            alert('Failed to mark prayer as answered. Please try again.');
            
            // Restore original Parse configuration on error
            const originalServerURL = 'https://forms.bethelchristiancenter.com/api/parse';
            const originalAppId = 'myAppId';
            Parse.serverURL = originalServerURL;
            Parse._initialize(originalAppId, null, null);
        }
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredRequests = prayerRequests.filter(request => {
        if (!searchTerm) return true;
        
        const searchLower = searchTerm.toLowerCase();
        const name = request.get('name')?.toLowerCase() || '';
        const prayerText = request.get('prayerRequest')?.toLowerCase() || '';
        const email = request.get('email')?.toLowerCase() || '';
        
        return name.includes(searchLower) || 
               prayerText.includes(searchLower) || 
               email.includes(searchLower);
    });

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div>
            <h2 className="text-2xl font-bold mb-6">Prayer Requests Management</h2>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Filters and Search */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search by name, email, or prayer request..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setFilter('all')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'all' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setFilter('unanswered')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'unanswered' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Unanswered
                        </button>
                        <button
                            onClick={() => setFilter('answered')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'answered' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Answered
                        </button>
                        <button
                            onClick={() => setFilter('private')}
                            className={`px-4 py-2 rounded-lg transition-colors ${
                                filter === 'private' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                            }`}
                        >
                            Private
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-gray-500">Total Requests</h3>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                        {prayerRequests.length}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-gray-500">Unanswered</h3>
                    <p className="text-2xl font-bold text-orange-600 mt-1">
                        {prayerRequests.filter(r => !r.get('prayed')).length}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-gray-500">Answered</h3>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                        {prayerRequests.filter(r => r.get('prayed')).length}
                    </p>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                    <h3 className="text-sm font-medium text-gray-500">Private</h3>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                        {prayerRequests.filter(r => r.get('private')).length}
                    </p>
                </div>
            </div>

            {/* Prayer Requests List */}
            <div className="space-y-4">
                {filteredRequests.length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                        No prayer requests found matching your criteria.
                    </div>
                ) : (
                    filteredRequests.map((request) => (
                        <div
                            key={request.id}
                            className={`bg-white rounded-lg shadow p-6 ${
                                request.get('prayed') ? 'border-l-4 border-green-500' : ''
                            }`}
                        >
                            <div className="flex justify-between items-start mb-4">
                                <div>
                                    <h3 className="font-semibold text-lg">
                                        {request.get('name') || 'Anonymous'}
                                    </h3>
                                    <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                                        <span>{formatDate(request.get('createdAt'))}</span>
                                        {request.get('email') && (
                                            <a 
                                                href={`mailto:${request.get('email')}`}
                                                className="text-blue-600 hover:underline"
                                            >
                                                {request.get('email')}
                                            </a>
                                        )}
                                        {request.get('phoneNumber') && (
                                            <span>{request.get('phoneNumber')}</span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {request.get('private') && (
                                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                                            Private
                                        </span>
                                    )}
                                    {request.get('prayed') ? (
                                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                                            Answered
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => markAsAnswered(request.id)}
                                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                                        >
                                            Mark as Answered
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            <div className="text-gray-700 whitespace-pre-wrap">
                                {request.get('prayerRequest')}
                            </div>
                            
                            {request.get('prayerCount') > 0 && (
                                <div className="mt-4 text-sm text-gray-500">
                                    🙏 {request.get('prayerCount')} people have prayed for this request
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default PrayerRequestsManager;